import React, { useState, useEffect } from 'react';

const DynamicPopupModal = ({ 
  isOpen, 
  onClose, 
  type = 'no-data', // 'no-data', 'no-plan', or 'custom'
  title,
  message,
  customIcon 
}) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
    } else {
      const timer = setTimeout(() => setIsVisible(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const getIcon = () => {
    switch (type) {
      case 'no-data':
        return '📊';
      case 'no-plan':
        return '📋';
      case 'custom':
        return customIcon || '⚠️';
      default:
        return '📊';
    }
  };

  const getDefaultTitle = () => {
    switch (type) {
      case 'no-data':
        return 'No Data Available';
      case 'no-plan':
        return 'No Active Plan';
      case 'custom':
        return 'Information';
      default:
        return 'Information';
    }
  };

  const getDefaultMessage = () => {
    switch (type) {
      case 'no-data':
        return 'There is currently no data to display. Please try again later or contact support.';
      case 'no-plan':
        return 'You don\'t have any active plans at the moment. Consider purchasing a new plan to get started.';
      case 'custom':
        return 'Please check the information and try again.';
      default:
        return 'Something went wrong. Please try again.';
    }
  };

  const getIconStyle = () => {
    const baseStyle = 'w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center text-4xl animate-pulse';
    
    switch (type) {
      case 'no-data':
        return `${baseStyle} bg-gradient-to-br from-red-400 to-red-600 text-white`;
      case 'no-plan':
        return `${baseStyle} bg-gradient-to-br from-orange-400 to-orange-600 text-white`;
      case 'custom':
        return `${baseStyle} bg-gradient-to-br from-blue-400 to-blue-600 text-white`;
      default:
        return `${baseStyle} bg-gradient-to-br from-gray-400 to-gray-600 text-white`;
    }
  };

  if (!isVisible) return null;

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-all duration-300 ${
      isOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
    }`}>
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className={`relative bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl transform transition-all duration-300 ${
        isOpen ? 'scale-100 translate-y-0' : 'scale-95 -translate-y-4'
      }`}>
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors duration-200"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Icon */}
        <div className={getIconStyle()}>
          {getIcon()}
        </div>

        {/* Content */}
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            {title || getDefaultTitle()}
          </h2>
          
          <p className="text-gray-600 mb-8 leading-relaxed">
            {message || getDefaultMessage()}
          </p>

          {/* Action Button */}
          <button
            onClick={onClose}
            className="w-full bg-blue-dark text-white py-3 px-6 rounded-lg font-semibold shadow-lg"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
};

export default DynamicPopupModal;